"use client";

import { Button } from "@/components/ui/button";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { useBoard } from "@/components/BoardProvider";
import { useConvexAuth, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@repo/backend/convex/_generated/api";
import { TEXTBOOK_MODULES, moduleForTopicId } from "@/lib/textbookModules";
import { stashReturnUrl } from "@/lib/authRedirect";
import Link from "next/link";

export type TextbookNav = FunctionReturnType<
  typeof api.textbook.topics.getNavigation
>;

export default function TextbookPromo({
  initialNav,
}: {
  initialNav?: TextbookNav | null;
}) {
  const { openAuthModal } = useAuthModal();
  const board = useBoard();
  const { isAuthenticated } = useConvexAuth();
  // `useQuery` is undefined during SSR, so fall back to the server-fetched nav
  // to keep the topic index in the initial HTML.
  const nav =
    useQuery(api.textbook.topics.getNavigation, { board: board.shortName }) ??
    initialNav;

  const counts: Record<string, number> = {};
  if (nav) {
    for (const subs of Object.values(nav.groupedTopics)) {
      for (const t of subs) {
        if (!t.slug) continue;
        const m = moduleForTopicId(t.topicId);
        if (m) counts[m.slug] = (counts[m.slug] ?? 0) + 1;
      }
    }
  }
  const modules = TEXTBOOK_MODULES.filter((m) => (counts[m.slug] ?? 0) > 0);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  const signUp = () => {
    stashReturnUrl(
      window.location.pathname + window.location.search + window.location.hash,
    );
    openAuthModal("signup");
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Textbook
      </p>
      <h1 className="mt-3 font-script text-4xl leading-none text-foreground md:text-5xl">
        The {board.shortName} textbook
      </h1>
      <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
        {total > 0 ? `${total} topics` : "Every topic"} for the {board.name},
        each worked through in full with clear explanations, worked examples,
        and the official examiner diagrams.
        {isAuthenticated ? "" : " Free to read, no account needed."}
      </p>

      {modules.length > 0 && (
        <ol className="mt-14">
          {modules.map((m, i) => (
            <li key={m.slug} className="border-t border-border">
              <Link
                href={`/textbook/${m.slug}`}
                className="group grid grid-cols-[3.5rem_1fr] gap-x-4 py-8 sm:grid-cols-[5.5rem_1fr] sm:gap-x-6"
              >
                <span
                  aria-hidden
                  className="font-script text-4xl leading-none text-primary sm:text-5xl"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-2xl font-medium text-foreground transition-colors duration-150 group-hover:text-primary">
                      {m.name}
                    </span>
                    <span className="shrink-0 text-sm tabular-nums text-muted-foreground transition-colors duration-150 group-hover:text-primary">
                      {counts[m.slug]} topics
                    </span>
                  </div>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
                    {m.blurb}
                  </p>
                </div>
              </Link>
            </li>
          ))}
          <li className="border-t border-border" />
        </ol>
      )}

      {!isAuthenticated && (
        <div className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
            A free account marks topics complete, bookmarks pages, and puts
            practice questions alongside each topic.
          </p>
          <Button onClick={signUp} className="shrink-0">
            Sign up free
          </Button>
        </div>
      )}
    </div>
  );
}
